import { WebTracerProvider } from '@opentelemetry/sdk-trace-web'
import { BatchSpanProcessor } from '@opentelemetry/sdk-trace-base'
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http'
import { registerInstrumentations } from '@opentelemetry/instrumentation'
import { DocumentLoadInstrumentation } from '@opentelemetry/instrumentation-document-load'
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch'
import { UserInteractionInstrumentation } from '@opentelemetry/instrumentation-user-interaction'
import { resourceFromAttributes } from '@opentelemetry/resources'
import { ATTR_SERVICE_NAME } from '@opentelemetry/semantic-conventions'
import { trace } from '@opentelemetry/api'
import { logs } from '@opentelemetry/api-logs'
import { LoggerProvider, BatchLogRecordProcessor } from '@opentelemetry/sdk-logs'
import { OTLPLogExporter } from '@opentelemetry/exporter-logs-otlp-http'
import { onCLS, onFCP, onINP, onLCP, onTTFB } from 'web-vitals'

export default defineNuxtPlugin(() => {
  const runtimeConfig = useRuntimeConfig()
  const serviceName = 'sec-auditor-web-browser'

  const resource = resourceFromAttributes({
    [ATTR_SERVICE_NAME]: serviceName,
    'app.version': runtimeConfig.public.VERSION as string,
    'app.environment': runtimeConfig.public.ENVIRONMENT as string
  })

  // sent to our own server-side proxy, which holds the OTLP credentials and forwards upstream
  const logExporter = new OTLPLogExporter({ url: '/api/telemetry/logs' })
  const loggerProvider = new LoggerProvider({ resource, processors: [new BatchLogRecordProcessor(logExporter)] })
  logs.setGlobalLoggerProvider(loggerProvider)

  const traceExporter = new OTLPTraceExporter({ url: '/api/telemetry/traces' })
  const provider = new WebTracerProvider({
    resource,
    spanProcessors: [new BatchSpanProcessor(traceExporter)]
  })

  provider.register()

  registerInstrumentations({
    instrumentations: [
      new DocumentLoadInstrumentation(),
      new UserInteractionInstrumentation(),
      new FetchInstrumentation({
        // only propagate trace headers to own API endpoints, not external services
        propagateTraceHeaderCorsUrls: [
          new RegExp(runtimeConfig.public.API_URL as string),
          /localhost/
        ]
      })
    ]
  })

  // Web Vitals → OTel spans
  const tracer = trace.getTracer('web-vitals', runtimeConfig.public.VERSION as string)

  const reportVital = (name: string, value: number, id: string, rating: string, navigationType?: string): void => {
    const span = tracer.startSpan('web_vital')
    span.setAttributes({
      'web_vital.name': name,
      'web_vital.value': value,
      'web_vital.id': id,
      'web_vital.rating': rating,
      ...(navigationType ? { 'web_vital.navigation_type': navigationType } : {})
    })
    span.end()
  }

  onCLS(m => reportVital('CLS', m.value, m.id, m.rating, m.navigationType))
  onFCP(m => reportVital('FCP', m.value, m.id, m.rating, m.navigationType))
  onINP(m => reportVital('INP', m.value, m.id, m.rating, m.navigationType))
  onLCP(m => reportVital('LCP', m.value, m.id, m.rating, m.navigationType))
  onTTFB(m => reportVital('TTFB', m.value, m.id, m.rating, m.navigationType))
})
